import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from './authStore';
import { isPublicPath } from './isPublicPath';
import { AppAccessLockedScreen } from './pages/AppAccessLockedScreen';
import { AuthLoadingScreen } from './pages/AuthLoadingScreen';
import { AuthLoginGate } from './pages/AuthLoginGate';

export interface AuthProviderProps {
  children: ReactNode;
  logoSrc?: string;
}

export function AuthProvider({ children, logoSrc }: AuthProviderProps) {
  const { restoreSession, status, isLoading, initialize } = useAuthStore();
  const location = useLocation();
  const isPublic = isPublicPath(location.pathname);

  useEffect(() => {
    if (isPublic) return;

    initialize();

    const { status: authStatus } = useAuthStore.getState();
    // After /loginok redeem the session is already applied — do not re-run boot logic.
    if (authStatus === 'authenticated' || authStatus === 'forbidden') {
      return;
    }

    void restoreSession();
  }, [restoreSession, initialize, isPublic]);

  if (isPublic) {
    return <>{children}</>;
  }

  if (status === 'loading' || isLoading) {
    return logoSrc ? <AuthLoadingScreen logoSrc={logoSrc} /> : <AuthLoadingScreen />;
  }

  // Signed in, but not entitled to this app at this location — a locked state, not a
  // bounce back to the login screen.
  if (status === 'forbidden') {
    return logoSrc ? <AppAccessLockedScreen logoSrc={logoSrc} /> : <AppAccessLockedScreen />;
  }

  if (status === 'unauthenticated') {
    return logoSrc ? <AuthLoginGate logoSrc={logoSrc} /> : <AuthLoginGate />;
  }

  return <>{children}</>;
}
